import { useLingui } from '@lingui/react/macro';
import {
  rowSelectionFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowSelectionState,
  type Updater,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Ellipsis,
  type LucideIcon,
} from 'lucide-react';
import { useId, useLayoutEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { parseOrderBy, toOrderBy, PAGE_SIZE_OPTIONS, type Page } from '@/shared/http';
import { useIsMobile } from '@/shared/mobile';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { cn } from '@/shared/ui/cn';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/drawer';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { Select } from '@/shared/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

import { mobileRoles, PRESET_META, sortFieldOf, type ColumnSpec, type MobileRole } from './columns';
import { readPath, resolvePatternText } from './text';
import { ColumnValue } from './value';

/** Paginação do legado: página a partir de 0 e tamanho em 5/10/15/20 (P02). */
export interface DataViewPagination {
  readonly pageIndex: number;
  readonly pageSize: number;
  readonly sizes?: readonly number[];
  readonly onChange: (next: { readonly pageIndex: number; readonly pageSize: number }) => void;
}

/** Ordenação no servidor: `orderBy=<campo>_ASC|DESC` (vazio = a ordem da API). */
export interface DataViewSort {
  readonly orderBy: string | undefined;
  readonly onChange: (orderBy: string | undefined) => void;
}

/** Seleção em lote (opcional). As ações recebem os ids selecionados. */
export interface DataViewSelection<Row = unknown> {
  readonly selected: readonly string[];
  readonly onChange: (ids: readonly string[]) => void;
  readonly actions?: (ids: readonly string[]) => ReactNode;
  /** `false` esconde a barra de seleção própria, quando a tela usa o `BulkBar` de `shared/action`. */
  readonly bar?: boolean;
  /**
   * Linha que não pode ser marcada: caixa desabilitada, e "selecionar todos" a ignora (o
   * `isRowSelectable` do DataGrid do legado). Ausente, toda linha pode.
   */
  readonly isSelectable?: ((row: Row) => boolean) | undefined;
}

/** Ação de uma linha (lápis, lixeira do legado): ícone na tabela, bottom sheet no iPhone. */
export interface DataViewRowAction {
  readonly id: string;
  /** Rótulo já traduzido: nome acessível, dica e item do sheet. */
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
  readonly tone?: 'default' | 'danger' | undefined;
  readonly onSelect: () => void;
  readonly disabled?: boolean | string;
}

export interface DataViewProps<Row> {
  readonly columns: readonly ColumnSpec[];
  readonly page: Page<Row>;
  /** Identidade estável da linha (armadilha 3 do 06: nada de `Math.random()`). Obrigatório. */
  readonly rowKey: string | ((row: Row) => string);
  /** Nome acessível da tabela ou da lista. */
  readonly label: string;
  readonly pagination?: DataViewPagination;
  readonly sort?: DataViewSort;
  readonly selection?: DataViewSelection<Row> | undefined;
  /** Abre o registro: clique na linha, Enter no título, toque no cartão. */
  readonly onOpen?: (row: Row) => void;
  /** Ações por linha (as já permitidas): coluna "Ações" na web, "…" com bottom sheet no cartão. */
  readonly rowActions?: (row: Row) => readonly DataViewRowAction[];
  /** Força a apresentação; o padrão segue o corte de 768 px (U12). */
  readonly presentation?: 'web' | 'mobile';
  /** Página anterior esmaecida enquanto a nova carrega (U39 §8). */
  readonly dimmed?: boolean;
  /** Borda e fundo de cartão na tabela; `false` dentro de um `BlockFrame`, que já é o cartão. */
  readonly framed?: boolean;
  /**
   * Altura da tabela na web: `viewport` (padrão) rola por dentro com o cabeçalho preso, cabendo na
   * área visível junto com a paginação; `content` cresce com as linhas.
   */
  readonly fit?: 'viewport' | 'content' | undefined;
  readonly className?: string;
}

const features = tableFeatures({ rowSelectionFeature });

// O TanStack pede `RowData` (objeto indexável); a linha do módulo entra como está e volta tipada no `onOpen`.
type TableRow = Record<string, unknown>;

function keyOf<Row>(rowKey: DataViewProps<Row>['rowKey'], row: Row): string {
  if (typeof rowKey === 'function') return rowKey(row);
  const value = readPath(row, rowKey);
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/** Próximo `orderBy` ao clicar no cabeçalho: sem ordem → crescente → decrescente → sem ordem. */
export function nextOrderBy(current: string | undefined, field: string): string | undefined {
  const parsed = parseOrderBy(current);
  if (parsed?.field !== field) return toOrderBy(field, 'asc');
  return parsed.direction === 'asc' ? toOrderBy(field, 'desc') : undefined;
}

/**
 * A seleção que vale: sem as linhas da página que `isSelectable` recusa, mesmo que o id venha de fora
 * (URL, agente). Id de fora da página fica, porque a seleção atravessa páginas e não há linha para julgar.
 */
function effectiveSelection<Row>(props: DataViewProps<Row>): DataViewSelection<Row> | undefined {
  const selection = props.selection;
  const isSelectable = selection?.isSelectable;
  if (!selection || !isSelectable) return selection;
  const blocked = new Set(props.page.items.filter((row) => !isSelectable(row)).map((row) => keyOf(props.rowKey, row)));
  if (!selection.selected.some((id) => blocked.has(id))) return selection;
  return { ...selection, selected: selection.selected.filter((id) => !blocked.has(id)) };
}

/**
 * Lista paginada no servidor (P02): tabela a partir de 768 px (TanStack Table) e cartões desenhados
 * no iPhone (U41), com os mesmos presets de coluna, paginação e ordenação do legado e seleção em lote
 * opcional. Não busca dado: recebe a `Page` pronta (use dentro de um bloco ou de uma tela).
 */
export function DataView<Row>(input: DataViewProps<Row>) {
  const isMobile = useIsMobile();
  const presentation = input.presentation ?? (isMobile ? 'mobile' : 'web');
  const selection = effectiveSelection(input);
  const props = selection === input.selection ? input : { ...input, selection };
  return (
    <div
      data-slot="data-view"
      data-presentation={presentation}
      className={cn('flex min-w-0 flex-col gap-3', props.className)}
    >
      {props.selection && props.selection.bar !== false && props.selection.selected.length > 0 ? (
        <SelectionBar selection={props.selection} mobile={presentation === 'mobile'} />
      ) : null}
      <div
        className={cn(
          'transition-opacity duration-(--lume-dur-fast)',
          props.dimmed ? 'opacity-55' : null,
        )}
      >
        {presentation === 'mobile' ? <DataCards {...props} /> : <DataTable {...props} />}
      </div>
      {props.pagination && props.page.total > 0 ? (
        <Pager
          label={props.label}
          pagination={props.pagination}
          total={props.page.total}
          pageCount={props.page.pageCount}
          mobile={presentation === 'mobile'}
        />
      ) : null}
    </div>
  );
}

function SelectionBar({
  selection,
  mobile,
}: {
  readonly selection: Pick<DataViewSelection, 'selected' | 'onChange' | 'actions'>;
  readonly mobile: boolean;
}) {
  const { t } = useLingui();
  const count = selection.selected.length;
  return (
    <div
      role="region"
      aria-label={t`Seleção em lote`}
      className={cn(
        'flex animate-fade-in flex-wrap items-center gap-2 rounded-card border border-border bg-accent-wash px-3 py-2 text-sm text-accent-text',
        mobile && 'sticky bottom-[calc(env(safe-area-inset-bottom,0px)+12px)] z-10 shadow-2',
      )}
    >
      <span className="font-medium" aria-live="polite">
        {count === 1 ? t`1 selecionado` : t`${count} selecionados`}
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {selection.actions?.(selection.selected)}
        <Button variant="ghost" size="sm" onClick={() => selection.onChange([])}>
          {t`Limpar seleção`}
        </Button>
      </div>
    </div>
  );
}

function SortIcon({ direction }: { readonly direction: 'asc' | 'desc' | undefined }) {
  if (direction === 'asc') return <ArrowUp aria-hidden className="size-3.5" />;
  if (direction === 'desc') return <ArrowDown aria-hidden className="size-3.5" />;
  return <ArrowUpDown aria-hidden className="size-3.5 opacity-50" />;
}

const EMPTY_SELECTION: RowSelectionState = {};

function rowName(
  columns: readonly ColumnSpec[],
  roles: readonly MobileRole[],
  row: unknown,
  id: string,
): string {
  const title = columns[roles.indexOf('title')];
  const value = title ? readPath(row, title.field) : undefined;
  return typeof value === 'string' && value.trim() !== '' ? value : id;
}

function RowActionButtons({
  actions,
  name,
}: {
  readonly actions: readonly DataViewRowAction[];
  readonly name: string;
}) {
  return (
    <span className="inline-flex items-center justify-end gap-0.5">
      {actions.map((action) => {
        const Icon = action.icon ?? Ellipsis;
        const label = `${action.label}: ${name}`;
        return (
          <Tooltip key={action.id}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                data-action={action.id}
                aria-label={label}
                aria-disabled={!!action.disabled || undefined}
                className={cn(
                  'size-8',
                  action.tone === 'danger' ? 'text-err hover:text-err' : 'text-muted-foreground',
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!action.disabled) action.onSelect();
                }}
              >
                <Icon aria-hidden className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {typeof action.disabled === 'string' ? action.disabled : action.label}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </span>
  );
}

// Abaixo da tabela ficam a paginação, o fundo do cartão e o respiro do `main`.
const FIT_MIN_HEIGHT = 320;
const FIT_BOTTOM_GAP = 64;

function scrollParentOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

/** Teto de altura para a tabela caber na área rolável da tela junto com o que vem logo abaixo dela. */
function useViewportFit(enabled: boolean) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number>();
  useLayoutEffect(() => {
    if (!enabled || !element) return;
    // O `viewport` do ScrollArea rola a própria tabela: a área da tela é o próximo acima dele.
    const scroller = element.parentElement ? scrollParentOf(element.parentElement) : null;
    const root = element.closest('[data-slot="data-view"]');
    const measure = () => {
      const rect = element.getBoundingClientRect();
      const top = scroller
        ? rect.top - scroller.getBoundingClientRect().top + scroller.scrollTop
        : rect.top + window.scrollY;
      const height = scroller ? scroller.clientHeight : window.innerHeight;
      const below = root ? Math.max(0, root.getBoundingClientRect().bottom - rect.bottom) : 0;
      setMaxHeight(Math.max(FIT_MIN_HEIGHT, Math.floor(height - top - below - FIT_BOTTOM_GAP)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller ?? document.body);
    if (scroller?.firstElementChild) observer.observe(scroller.firstElementChild);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, element]);
  return { ref: setElement, maxHeight: enabled ? maxHeight : undefined };
}

// Cabeçalho preso: a borda vira sombra porque `border-collapse` não acompanha a célula sticky.
const HEAD_CELL = 'sticky top-0 bg-card shadow-[inset_0_-1px_0_var(--color-structural)]';

function DataTable<Row>({
  columns,
  page,
  rowKey,
  label,
  sort,
  selection,
  onOpen,
  rowActions,
  framed = true,
  fit = 'viewport',
}: DataViewProps<Row>) {
  const { i18n, t } = useLingui();
  const viewport = useViewportFit(fit === 'viewport');
  const actionWidth = rowActions
    ? Math.max(56, ...page.items.map((row) => rowActions(row).length * 34 + 16))
    : 0;
  const pinStyle = (column: ColumnSpec, head = false): CSSProperties | undefined => {
    if (!column.pin) return undefined;
    const index = columns.indexOf(column);
    const neighbors = columns.filter(
      (item, i) => item.pin === column.pin && (column.pin === 'left' ? i < index : i > index),
    );
    const offset =
      neighbors.reduce((sum, item) => sum + (item.width ?? 200), 0) +
      (column.pin === 'right' ? actionWidth : selection ? 40 : 0);
    return {
      position: 'sticky',
      [column.pin]: offset,
      width: column.width ?? 200,
      minWidth: column.width ?? 200,
      maxWidth: column.width ?? 200,
      zIndex: head ? 30 : 1,
    };
  };
  const roles = mobileRoles(columns);
  const titleIndex = roles.indexOf('title');
  const sorted = parseOrderBy(sort?.orderBy);
  const rowSelection: RowSelectionState = selection
    ? Object.fromEntries(selection.selected.map((id) => [id, true]))
    : EMPTY_SELECTION;

  const defs: ColumnDef<typeof features, TableRow>[] = columns.map((column) => ({
    id: column.field,
    accessorFn: (row: TableRow) => readPath(row, column.field),
    header: resolvePatternText(i18n, column.label),
  }));

  const table = useTable({
    features,
    columns: defs,
    data: page.items as unknown as TableRow[],
    getRowId: (row) => keyOf(rowKey, row as Row),
    enableRowSelection: selection?.isSelectable
      ? (row: { readonly original: TableRow }) => selection.isSelectable?.(row.original as Row) ?? true
      : selection !== undefined,
    state: { rowSelection },
    onRowSelectionChange: (updater: Updater<RowSelectionState>) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      selection?.onChange(Object.keys(next).filter((id) => next[id]));
    },
  });

  const rows = table.getRowModel().rows;
  const selectableRows = rows.filter((row) => row.getCanSelect());
  const allSelected = selectableRows.length > 0 && selectableRows.every((row) => row.getIsSelected());
  const someSelected = selectableRows.some((row) => row.getIsSelected());

  return (
    // A tabela rola por dentro (X e Y): a região precisa de foco para quem usa teclado (axe `scrollable-region-focusable`).
    <ScrollArea
      axes="both"
      className={cn(framed && 'rounded-card border border-border bg-card')}
      viewportRef={viewport.ref}
      viewport={{
        role: 'region',
        'aria-label': label,
        tabIndex: 0,
        style: viewport.maxHeight ? { maxHeight: viewport.maxHeight } : undefined,
        className: 'overscroll-x-contain focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
      }}
    >
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{label}</caption>
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            {selection ? (
              <th scope="col" data-pin="selection" className={cn(HEAD_CELL, 'left-0 z-30 w-10 min-w-10 max-w-10 px-3 py-2.5')}>
                <Checkbox
                  aria-label={t`Selecionar todos desta página`}
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  disabled={selectableRows.length === 0}
                  onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
                />
              </th>
            ) : null}
            {columns.map((column) => {
              const field = sortFieldOf(column);
              const direction = sorted?.field === field ? sorted.direction : undefined;
              const align = PRESET_META[column.preset].align;
              const text = resolvePatternText(i18n, column.label);
              return (
                <th
                  key={column.field}
                  scope="col"
                  style={pinStyle(column, true)}
                  data-pin={column.pin}
                  aria-sort={
                    direction === 'asc'
                      ? 'ascending'
                      : direction === 'desc'
                        ? 'descending'
                        : undefined
                  }
                  className={cn(
                    HEAD_CELL,
                    'z-20 px-3 py-2.5 font-medium whitespace-nowrap',
                    align === 'end' && 'text-right',
                  )}
                >
                  {column.sortable && sort ? (
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
                        direction && 'text-foreground',
                      )}
                      onClick={() => sort.onChange(nextOrderBy(sort.orderBy, field))}
                    >
                      {text}
                      <SortIcon direction={direction} />
                    </button>
                  ) : (
                    text
                  )}
                </th>
              );
            })}
            {rowActions ? (
              <th
                scope="col"
                data-pin="right"
                style={{ width: actionWidth, minWidth: actionWidth }}
                className={cn(HEAD_CELL, 'right-0 z-30 px-3 py-2.5 text-right font-medium whitespace-nowrap')}
              >
                {t`Ações`}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = row.getIsSelected();
            return (
              <tr
                key={row.id}
                data-selected={selected || undefined}
                className={cn(
                  'group h-row border-b border-border transition-colors last:border-b-0 hover:bg-muted data-selected:bg-accent-wash/60 [&:hover_.text-muted-foreground]:text-foreground [&[data-selected]_.text-muted-foreground]:text-foreground',
                  onOpen && 'cursor-pointer',
                )}
                onClick={onOpen ? () => onOpen(row.original as Row) : undefined}
              >
                {selection ? (
                  <td data-pin="selection" className="sticky left-0 z-10 w-10 min-w-10 max-w-10 bg-card px-3 group-hover:bg-muted group-data-selected:bg-accent-wash" onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                      aria-label={t`Selecionar ${rowName(columns, roles, row.original, row.id)}`}
                      checked={selected}
                      disabled={!row.getCanSelect()}
                      onCheckedChange={(value) => row.toggleSelected(value === true)}
                    />
                  </td>
                ) : null}
                {columns.map((column, index) => {
                  const align = PRESET_META[column.preset].align;
                  const isTitle = index === titleIndex;
                  return (
                    <td
                      key={column.field}
                      data-pin={column.pin}
                      style={pinStyle(column)}
                      className={cn(
                        'px-3 whitespace-nowrap',
                        column.pin &&
                          'bg-card group-hover:bg-muted group-data-selected:bg-accent-wash',
                        align === 'end' && 'text-right',
                        isTitle && 'max-w-[22rem] truncate font-medium text-foreground',
                      )}
                    >
                      {isTitle && onOpen ? (
                        <button
                          type="button"
                          className="max-w-full truncate rounded-sm text-left underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpen(row.original as Row);
                          }}
                        >
                          <ColumnValue column={column} row={row.original} />
                        </button>
                      ) : (
                        <ColumnValue column={column} row={row.original} />
                      )}
                    </td>
                  );
                })}
                {rowActions ? (
                  <td
                    data-pin="right"
                    style={{ width: actionWidth, minWidth: actionWidth }}
                    className="sticky right-0 z-10 bg-card px-2 text-right whitespace-nowrap group-hover:bg-muted group-data-selected:bg-accent-wash"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <RowActionButtons
                      actions={rowActions(row.original as Row)}
                      name={rowName(columns, roles, row.original, row.id)}
                    />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollArea>
  );
}

function pick(columns: readonly ColumnSpec[], roles: readonly MobileRole[], role: MobileRole) {
  return columns.filter((_, index) => roles[index] === role);
}

/** "Ações da linha" do iPhone (protótipo `m_row_actions`): título = o registro, linhas de 56 px. */
function RowActionsSheet({
  actions,
  name,
  subtitle,
  open,
  onOpenChange,
}: {
  readonly actions: readonly DataViewRowAction[];
  readonly name: string;
  readonly subtitle?: ReactNode;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-slot="row-actions">
        <DrawerHeader>
          <DrawerTitle>{name}</DrawerTitle>
          <DrawerDescription>{subtitle ?? t`Ações do registro`}</DrawerDescription>
        </DrawerHeader>
        <DrawerBody className="pt-0 pb-4">
          <ul className="flex flex-col">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <li key={action.id}>
                  <button
                    type="button"
                    data-action={action.id}
                    aria-disabled={!!action.disabled || undefined}
                    className={cn(
                      'flex min-h-14 w-full items-center gap-3.5 text-left text-base',
                      action.tone === 'danger' ? 'text-err' : 'text-foreground',
                    )}
                    onClick={() => {
                      if (action.disabled) return;
                      onOpenChange(false);
                      action.onSelect();
                    }}
                  >
                    {Icon ? (
                      <Icon aria-hidden className="size-[22px] shrink-0" />
                    ) : (
                      <span aria-hidden className="size-[22px] shrink-0" />
                    )}
                    <span>
                      {action.label}
                      {typeof action.disabled === 'string' ? (
                        <span className="block text-sm text-muted-foreground">
                          {action.disabled}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function DataCards<Row>({
  columns,
  page,
  rowKey,
  label,
  selection,
  onOpen,
  rowActions,
}: DataViewProps<Row>) {
  const { t } = useLingui();
  // O último registro fica guardado depois de fechar: a saída do sheet anima com o mesmo título (U38).
  const [actionsFor, setActionsFor] = useState<{ readonly row: Row; readonly id: string } | null>(
    null,
  );
  const [actionsOpen, setActionsOpen] = useState(false);
  const roles = mobileRoles(columns);
  const title = pick(columns, roles, 'title')[0];
  const subtitle = pick(columns, roles, 'subtitle')[0];
  const badges = pick(columns, roles, 'badge');
  const amount = pick(columns, roles, 'amount')[0];
  const meta = pick(columns, roles, 'meta');
  const selected = new Set(selection?.selected ?? []);

  const list = (
    <ul aria-label={label} className="flex flex-col gap-2">
      {page.items.map((row) => {
        const id = keyOf(rowKey, row);
        const isSelected = selected.has(id);
        const body = (
          <>
            <span className="flex items-start gap-2">
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                {title ? (
                  <ColumnValue
                    column={title}
                    row={row}
                    className="truncate text-base font-semibold text-foreground"
                  />
                ) : null}
                {subtitle ? (
                  <ColumnValue
                    column={subtitle}
                    row={row}
                    className="truncate text-sm text-muted-foreground"
                  />
                ) : null}
              </span>
              {onOpen ? (
                <ChevronRight
                  aria-hidden
                  className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                />
              ) : null}
            </span>
            {badges.length > 0 || amount ? (
              <span className="flex flex-wrap items-center gap-1.5">
                {badges.map((column) => (
                  <ColumnValue key={column.field} column={column} row={row} />
                ))}
                {amount ? (
                  <ColumnValue
                    column={amount}
                    row={row}
                    className="ml-auto text-base font-semibold text-foreground"
                  />
                ) : null}
              </span>
            ) : null}
            {meta.length > 0 ? (
              <span className="flex flex-wrap justify-end gap-x-1.5 text-xs text-muted-foreground">
                {meta.map((column, index) => (
                  <span key={column.field} className="inline-flex gap-1.5">
                    {index > 0 ? <span aria-hidden>·</span> : null}
                    <ColumnValue column={column} row={row} />
                  </span>
                ))}
              </span>
            ) : null}
          </>
        );
        return (
          <li
            key={id}
            data-selected={isSelected || undefined}
            className="flex items-start gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 transition-colors data-selected:border-accent data-selected:bg-accent-wash/60 [&[data-selected]_.text-muted-foreground]:text-foreground"
          >
            {selection ? (
              <Checkbox
                className="mt-1"
                aria-label={t`Selecionar ${rowName(columns, roles, row, id)}`}
                checked={isSelected}
                disabled={selection.isSelectable ? !selection.isSelectable(row) : false}
                onCheckedChange={(value) =>
                  selection.onChange(
                    value === true
                      ? [...selected, id]
                      : [...selected].filter((other) => other !== id),
                  )
                }
              />
            ) : null}
            {onOpen ? (
              <button
                type="button"
                className="flex min-w-0 flex-1 flex-col gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onOpen(row)}
              >
                {body}
              </button>
            ) : (
              <div className="flex min-w-0 flex-1 flex-col gap-2">{body}</div>
            )}
            {rowActions && rowActions(row).length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mt-1 -mr-2 shrink-0 text-muted-foreground"
                aria-label={t`Ações de ${rowName(columns, roles, row, id)}`}
                onClick={() => {
                  setActionsFor({ row, id });
                  setActionsOpen(true);
                }}
              >
                <Ellipsis aria-hidden />
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
  if (!rowActions || !actionsFor) return list;
  return (
    <>
      {list}
      <RowActionsSheet
        actions={rowActions(actionsFor.row)}
        name={rowName(columns, roles, actionsFor.row, actionsFor.id)}
        subtitle={subtitle ? <ColumnValue column={subtitle} row={actionsFor.row} /> : undefined}
        open={actionsOpen}
        onOpenChange={setActionsOpen}
      />
    </>
  );
}

export function Pager({
  label,
  pagination,
  total,
  pageCount,
  mobile,
}: {
  readonly label: string;
  readonly pagination: DataViewPagination;
  readonly total: number;
  readonly pageCount: number;
  readonly mobile: boolean;
}) {
  const { t } = useLingui();
  const sizeLabelId = useId();
  const { pageIndex, pageSize, onChange } = pagination;
  const last = Math.max(0, pageCount - 1);
  const from = Math.min(total, pageIndex * pageSize + 1);
  const to = Math.min(total, (pageIndex + 1) * pageSize);
  const go = (index: number) =>
    onChange({ pageIndex: Math.min(Math.max(0, index), last), pageSize });
  const sizes = pagination.sizes ?? PAGE_SIZE_OPTIONS;
  const range = t`${from}–${to} de ${total}`;
  // Duas listas paginadas na mesma tela precisam de marcos com nomes distintos (axe `landmark-unique`).
  const navLabel = t`Paginação de ${label}`;

  if (mobile) {
    return (
      <nav aria-label={navLabel} className="flex items-center justify-between gap-2">
        <Button
          variant="secondary"
          size="icon"
          aria-label={t`Página anterior`}
          disabled={pageIndex <= 0}
          onClick={() => go(pageIndex - 1)}
        >
          <ChevronLeft aria-hidden />
        </Button>
        <span className="text-sm text-muted-foreground" aria-live="polite">
          {range}
        </span>
        <Button
          variant="secondary"
          size="icon"
          aria-label={t`Próxima página`}
          disabled={pageIndex >= last}
          onClick={() => go(pageIndex + 1)}
        >
          <ChevronRight aria-hidden />
        </Button>
      </nav>
    );
  }

  return (
    <nav aria-label={navLabel} className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
      <span className="flex items-center gap-2">
        <span id={sizeLabelId} className="text-muted-foreground">
          {t`Linhas por página`}
        </span>
        <Select
          aria-labelledby={sizeLabelId}
          className="w-20"
          value={String(pageSize)}
          options={sizes.map((size) => ({ value: String(size), label: String(size) }))}
          onChange={(value) => onChange({ pageIndex: 0, pageSize: Number(value) })}
        />
      </span>
      <span className="ml-auto text-muted-foreground" aria-live="polite">
        {range}
      </span>
      <span className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Primeira página`}
          disabled={pageIndex <= 0}
          onClick={() => go(0)}
        >
          <ChevronsLeft aria-hidden />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Página anterior`}
          disabled={pageIndex <= 0}
          onClick={() => go(pageIndex - 1)}
        >
          <ChevronLeft aria-hidden />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Próxima página`}
          disabled={pageIndex >= last}
          onClick={() => go(pageIndex + 1)}
        >
          <ChevronRight aria-hidden />
        </Button>
        {/* O legado ia para setPage(rowCount) (bug de precedência, 05 §2.5); aqui é a última de fato. */}
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Última página`}
          disabled={pageIndex >= last}
          onClick={() => go(last)}
        >
          <ChevronsRight aria-hidden />
        </Button>
      </span>
    </nav>
  );
}
